/**
 * Browser-side Web Push enrolment. The Functions API and `public/sw.js`
 * already speak Web Push; this is the missing link that asks the user for
 * permission, subscribes the service worker and registers the endpoint.
 */

import type { INotificationsClient } from '../services/clients/types';

export type PushSupportState = 'unsupported' | 'denied' | 'subscribed' | 'not-subscribed';

export interface PushBrowserEnv {
  navigator?: Pick<Navigator, 'serviceWorker'> | undefined;
  Notification?: {
    permission: NotificationPermission;
    requestPermission(): Promise<NotificationPermission>;
  };
  PushManager?: unknown;
}

function browserEnv(): PushBrowserEnv {
  if (typeof window === 'undefined') return {};
  return {
    navigator: typeof navigator === 'undefined' ? undefined : navigator,
    Notification: typeof Notification === 'undefined' ? undefined : Notification,
    PushManager: (window as unknown as { PushManager?: unknown }).PushManager,
  };
}

export function isPushSupported(env: PushBrowserEnv = browserEnv()): boolean {
  return Boolean(
    env.navigator && 'serviceWorker' in env.navigator && env.Notification && env.PushManager,
  );
}

/** Decode a base64url VAPID key into the `applicationServerKey` bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function currentRegistration(
  env: PushBrowserEnv,
): Promise<ServiceWorkerRegistration | undefined> {
  const sw = env.navigator?.serviceWorker;
  if (!sw) return undefined;
  return (await sw.getRegistration()) ?? (await sw.ready);
}

export async function getPushSupportState(
  env: PushBrowserEnv = browserEnv(),
): Promise<PushSupportState> {
  if (!isPushSupported(env)) return 'unsupported';
  if (env.Notification!.permission === 'denied') return 'denied';
  try {
    const registration = await currentRegistration(env);
    const existing = await registration?.pushManager.getSubscription();
    return existing ? 'subscribed' : 'not-subscribed';
  } catch {
    return 'not-subscribed';
  }
}

export type EnablePushResult =
  | { ok: true; alreadySubscribed: boolean }
  | { ok: false; reason: 'unsupported' | 'denied' | 'no-registration' | 'error'; message?: string };

/**
 * Request permission, subscribe via the service worker with the server's
 * VAPID key and register the subscription for the signed-in user.
 * Re-registering an existing subscription is safe (the server upserts by endpoint).
 */
export async function enablePushNotifications(
  client: INotificationsClient,
  env: PushBrowserEnv = browserEnv(),
): Promise<EnablePushResult> {
  if (!isPushSupported(env)) return { ok: false, reason: 'unsupported' };
  const permission =
    env.Notification!.permission === 'granted'
      ? 'granted'
      : await env.Notification!.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };
  try {
    const registration = await currentRegistration(env);
    if (!registration) return { ok: false, reason: 'no-registration' };
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array((await client.getVapidPublicKey()).data.key),
      }));
    await client.registerPushSubscription(subscription.toJSON());
    return { ok: true, alreadySubscribed: Boolean(existing) };
  } catch (err) {
    return {
      ok: false,
      reason: 'error',
      message: err instanceof Error ? err.message : String(err),
    };
  }
}
